// Barcode symbology specs, data validation, check digits, bwip-js options and box geometry.
// Pure module (no window/document). Lengths are file units (HIMETRIC = 1/100 mm).

/** SmartDate X65 resolution (CoLOS DeviceCaps SDX65iw: X_DPI = Y_DPI = 305). */
export const PRINTER_DPI = 305;

export const TEXT_DISPLAY_OPTIONS = Object.freeze(['DISPLAY_DATA', 'DISPLAY_WITH_CHECKDIGIT', 'DO_NOT_DISPLAY']);
export const CODE_SETS = Object.freeze(['Auto', 'CodeA', 'CodeB', 'CodeC']);
export const DATAMATRIX_ECC = Object.freeze(['ECC_NONE', 'ECC_50', 'ECC_80', 'ECC_100', 'ECC_140', 'ECC_200']);
export const QR_ECC = Object.freeze(['QREC_0', 'QREC_1', 'QREC_2', 'QREC_3']);

/** Field property -> XML element name (for schema/label writers). */
export const FIELD_TO_XML = Object.freeze({
  barHeight: 'BarHeight',
  quietMargin: 'QuietMargin',
  narrowBarWidth: 'NarrowBarWidth',
  autoCheckDigit: 'AutoCheckDigit',
  textDisplay: 'TextDisplayOptions',
  codeSet: 'CodeSet',
  cellWidth: 'CellWidth',
  errorCorrection: 'ErrorCorrection',
});

const ONE_D = { barHeight: 1000, quietMargin: true, narrowBarWidth: 33, autoCheckDigit: true };

// `defaults` use Field property names (see FIELD_TO_XML). NarrowBarWidth 33 = 4 dots at 305 dpi
// (≈ 0.33 mm, the EAN nominal X-dimension); CellWidth 50 = 6 dots.
// element = render-style element (XSD RenderStyleChoice / EAN128RenderStyleChoice);
// typeAttr = type name registered in the SmartDate driver.
export const SYMBOLOGIES = Object.freeze([
  {
    id: 'EAN13', label: 'EAN-13', element: 'EAN13', typeAttr: 'EAN13.V1', oneD: true,
    defaults: { ...ONE_D, textDisplay: 'DISPLAY_WITH_CHECKDIGIT' },
    dataHint: '12 digits, e.g. 400638133393 (check digit added automatically)',
  },
  {
    id: 'EAN8', label: 'EAN-8', element: 'EAN8', typeAttr: 'EAN8.V1', oneD: true,
    defaults: { ...ONE_D, textDisplay: 'DISPLAY_WITH_CHECKDIGIT' },
    dataHint: '7 digits, e.g. 9638507 (check digit added automatically)',
  },
  {
    id: 'Code128', label: 'Code 128', element: 'Code128', typeAttr: 'Code128.V1', oneD: true,
    defaults: { ...ONE_D, textDisplay: 'DISPLAY_DATA', codeSet: 'Auto' },
    dataHint: 'Up to 80 letters, digits or punctuation',
  },
  {
    // GS1-128: <Code128Ean> render style; its data source is AppIDDataList or Static.
    id: 'Code128Ean', label: 'GS1-128', element: 'Code128Ean', typeAttr: 'Code128Ean.V1', oneD: true,
    defaults: { ...ONE_D, textDisplay: 'DISPLAY_DATA', codeSet: 'Auto' },
    dataHint: 'GS1 application identifiers, e.g. (01)10012345678902(10)LOT1',
  },
  {
    id: 'DataMatrix', label: 'Data Matrix', element: 'DataMatrix', typeAttr: 'DataMatrix.V1', oneD: false,
    defaults: { quietMargin: true, cellWidth: 50, errorCorrection: 'ECC_200' },
    dataHint: 'Up to 500 characters',
  },
  {
    id: 'QR', label: 'QR code', element: 'QR', typeAttr: 'GS1QR.V1', oneD: false,
    defaults: { quietMargin: true, cellWidth: 50, errorCorrection: 'QREC_1' },
    dataHint: 'Up to 500 characters, e.g. a web address',
  },
]);

const BY_ID = new Map(SYMBOLOGIES.map((s) => [s.id, s]));
export function symbologyById(id) { return BY_ID.get(id) || null; }
export function symbologyByElement(element) { return SYMBOLOGIES.find((s) => s.element === element) || null; }

// ---- printer dots ----------------------------------------------------------------------------

/** Units per printer dot (2540 / dpi; 8.328 at 305 dpi). */
export function unitsPerDot(dpi = PRINTER_DPI) { return 2540 / dpi; }

/** Round a length to whole printer dots (min 1 dot), returned as integer units. 33 → 33 (4 dots). */
export function snapNarrowBarWidth(units, dpi = PRINTER_DPI) {
  const dot = unitsPerDot(dpi);
  const dots = Math.max(1, Math.round((Number(units) || 0) / dot));
  return Math.round(dots * dot);
}

// ---- check digits ------------------------------------------------------------------------------

/**
 * GS1 mod-10 check digit for a digit string WITHOUT its check digit (any length: 7 for EAN-8,
 * 11 UPC-A, 12 EAN-13, 13 GTIN-14, 17 SSCC). Weights 3,1,3,… starting from the rightmost digit.
 */
export function gtinCheckDigit(digits) {
  const s = String(digits);
  if (!/^\d+$/.test(s)) throw new TypeError('gtinCheckDigit expects digits only');
  let sum = 0;
  for (let i = s.length - 1, w = 3; i >= 0; i--, w = w === 3 ? 1 : 3) sum += (s.charCodeAt(i) - 48) * w;
  return (10 - (sum % 10)) % 10;
}

// ---- validation ------------------------------------------------------------------------------

const ok = (normalized, checkDigit) => (checkDigit === undefined ? { ok: true, normalized } : { ok: true, normalized, checkDigit });
const bad = (message) => ({ ok: false, message });

function validateEan(label, len, data, auto) {
  const s = String(data ?? '');
  if (!s) return bad(auto ? `${label} needs ${len - 1} digits (check digit is added automatically)` : `${label} needs ${len} digits including the check digit`);
  if (!/^\d+$/.test(s)) return bad(`${label} can only contain digits`);
  if (auto) {
    if (s.length !== len - 1) return bad(`${label} needs ${len - 1} digits (check digit is added automatically)`);
    return ok(s, gtinCheckDigit(s));
  }
  if (s.length !== len) return bad(`${label} needs ${len} digits including the check digit`);
  const cd = gtinCheckDigit(s.slice(0, -1));
  if (cd !== Number(s[len - 1])) return bad(`${label} check digit should be ${cd}, not ${s[len - 1]}`);
  return ok(s, cd);
}

// GS1 AIs whose data has a predefined length (no FNC1 separator needed after them), by 2-digit prefix.
const GS1_FIXED_PREFIX = new Set(['00', '01', '02', '03', '04', '11', '12', '13', '14', '15', '16', '17', '18', '19', '20', '31', '32', '33', '34', '35', '36', '41']);

/** GS1 AI encodable character set 82 (GS1 General Specifications, figure 7.11-1). */
const CSET82 = /^[A-Za-z0-9!"%&'()*+,\-./:;<=>?_]+$/;
const CSET82_TEXT = 'letters, digits and ! " % & \' ( ) * + , - . / : ; < = > ? _';

/**
 * Supported AIs. kind: 'n' digits (min..max, check = GS1 mod-10 on the last digit), 'x' CSET 82 (min..max),
 * 'date' YYMMDD (DD = 00 allowed), 'datetime' YYMMDDhhmm, 'gcp' CSET 82 starting with ≥ 4 digits.
 * Every entry is accepted by bwip-js 4.5 (verified by the property test); anything else is rejected, because
 * bwip-js refuses unknown AIs and so would the printer's GS1 check.
 */
const GS1_AIS = [
  { re: /^00$/, kind: 'n', min: 18, max: 18, check: true },
  { re: /^0[12]$/, kind: 'n', min: 14, max: 14, check: true },
  { re: /^(10|21|22)$/, kind: 'x', min: 1, max: 20 },
  { re: /^1[123567]$/, kind: 'date' },
  { re: /^20$/, kind: 'n', min: 2, max: 2 },
  { re: /^(30|37)$/, kind: 'n', min: 1, max: 8 },
  { re: /^(240|241|400|90)$/, kind: 'x', min: 1, max: 30 },
  // 310n–316n, 320n–337n, 340n–357n, 360n–369n with n = 0..5 (decimal point position).
  { re: /^3(1[0-6]|2\d|3[0-7]|4\d|5[0-7]|6\d)[0-5]$/, kind: 'n', min: 6, max: 6 },
  { re: /^41[0-7]$/, kind: 'n', min: 13, max: 13, check: true },
  { re: /^7003$/, kind: 'datetime' },
  { re: /^8004$/, kind: 'gcp', min: 1, max: 30 },
  { re: /^9[1-9]$/, kind: 'x', min: 1, max: 90 },
];

/** Pairs that must not appear in the same barcode (GS1 invalid pairs; bwip-js GS1exclusiveAIs). */
const GS1_EXCLUSIVE = [['01', '37'], ['01', '02']];

const DAYS_IN_MONTH = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

/** YYMMDD with a real month/day; DD = 00 allowed when allowDay00. YY % 4 = 0 is a leap year (2000–2099 window). */
function isGs1Date(v, allowDay00) {
  if (!/^\d{6}$/.test(v)) return false;
  const yy = Number(v.slice(0, 2)), mm = Number(v.slice(2, 4)), dd = Number(v.slice(4, 6));
  if (mm < 1 || mm > 12) return false;
  if (dd === 0) return allowDay00;
  return dd <= (mm === 2 ? (yy % 4 === 0 ? 29 : 28) : DAYS_IN_MONTH[mm - 1]);
}

/** Parse "(01)123…(10)ABC" → [{ai, value}] or null when the syntax is wrong. The raw string is parsed (no trimming). */
export function parseGs1(data) {
  const s = String(data ?? '');
  const re = /\((\d{2,4})\)([^()]+)/y;
  const out = [];
  let m;
  re.lastIndex = 0;
  while (re.lastIndex < s.length && (m = re.exec(s))) out.push({ ai: m[1], value: m[2] });
  if (!out.length || re.lastIndex !== s.length) return null;
  return out;
}

function validateGs1(data) {
  const parts = parseGs1(data);
  if (!parts) return bad('GS1-128 needs application identifiers like (01)10012345678902');
  let checkDigit;
  const seen = new Set();
  for (const { ai, value } of parts) {
    if (seen.has(ai)) return bad(`GS1 (${ai}) appears more than once`);
    seen.add(ai);
    const rule = GS1_AIS.find((r) => r.re.test(ai));
    if (!rule) return bad(`GS1 (${ai}) isn't an application identifier this editor supports`);
    switch (rule.kind) {
      case 'n': {
        const need = rule.min === rule.max ? `${rule.min} digits` : `${rule.min} to ${rule.max} digits`;
        if (!/^\d+$/.test(value) || value.length < rule.min || value.length > rule.max) return bad(`GS1 (${ai}) needs ${need}`);
        if (rule.check) {
          const cd = gtinCheckDigit(value.slice(0, -1));
          if (cd !== Number(value.at(-1))) return bad(`GS1 (${ai}) check digit should be ${cd}, not ${value.at(-1)}`);
          if (ai === '01' && checkDigit === undefined) checkDigit = cd;
        }
        break;
      }
      case 'x':
        if (!CSET82.test(value)) return bad(`GS1 (${ai}) can only hold ${CSET82_TEXT}`);
        if (value.length < rule.min || value.length > rule.max) return bad(`GS1 (${ai}) needs ${rule.min} to ${rule.max} characters`);
        break;
      case 'gcp':
        if (!CSET82.test(value)) return bad(`GS1 (${ai}) can only hold ${CSET82_TEXT}`);
        if (!/^\d{4}/.test(value) || value.length > rule.max) {
          return bad(`GS1 (${ai}) needs a company prefix of at least 4 digits, then up to ${rule.max} characters in all`);
        }
        break;
      case 'date':
        if (!isGs1Date(value, true)) return bad(`GS1 (${ai}) needs a date as YYMMDD`);
        break;
      case 'datetime':
        if (!/^\d{10}$/.test(value) || !isGs1Date(value.slice(0, 6), false)
          || Number(value.slice(6, 8)) > 23 || Number(value.slice(8, 10)) > 59) {
          return bad(`GS1 (${ai}) needs a date and time as YYMMDDhhmm`);
        }
        break;
    }
  }
  for (const [a, b] of GS1_EXCLUSIVE) {
    if (seen.has(a) && seen.has(b)) return bad(`GS1 (${a}) and (${b}) can't be used together`);
  }
  const dataChars = parts.reduce((n, p) => n + p.ai.length + p.value.length, 0);
  if (dataChars > 48) return bad('GS1-128 can hold at most 48 characters');
  const normalized = parts.map((p) => `(${p.ai})${p.value}`).join('');
  return ok(normalized, checkDigit);
}

/** QR byte-mode capacity (UTF-8 bytes) of version 40 per error-correction level (bwip-js limits, measured). */
const QR_MAX_BYTES = Object.freeze({ QREC_0: 2953, QREC_1: 2331, QREC_2: 1663, QREC_3: 1273 });

const SPACES_MESSAGE = 'Remove spaces from the barcode data';
const INVISIBLE_MESSAGE = 'Remove invisible characters from the barcode data';

/**
 * First reason raw barcode data is unusable regardless of symbology, or null:
 * - whitespace at either end, or any line/tab-type whitespace inside (never trimmed silently);
 * - C0 controls, DEL, C1 controls, lone surrogates, U+FFFE/U+FFFF (cannot be encoded/saved).
 */
function rawDataProblem(s) {
  if (/^\s|\s$/.test(s) || /[\t\n\v\f\r\u2028\u2029]/.test(s)) return SPACES_MESSAGE;
  for (const ch of s) {
    const cp = ch.codePointAt(0);
    if (cp < 0x20 || (cp >= 0x7f && cp <= 0x9f) || (cp >= 0xd800 && cp <= 0xdfff) || cp === 0xfffe || cp === 0xffff) {
      return INVISIBLE_MESSAGE;
    }
  }
  return null;
}

/**
 * Validate barcode data exactly as it will be saved (no trimming). autoCheckDigit defaults to the symbology
 * default (true); errorCorrection (QR only) defaults to the symbology default and bounds the byte capacity.
 * Returns { ok, message?, normalized?, checkDigit? }.
 */
export function validateData(symbology, data, { autoCheckDigit, errorCorrection } = {}) {
  const spec = symbologyById(symbology);
  if (!spec) return bad('Unknown barcode type');
  const auto = autoCheckDigit ?? spec.defaults.autoCheckDigit ?? true;
  const s = String(data ?? '');
  const raw = rawDataProblem(s);
  if (raw) return bad(raw);
  // EAN and GS1-128 have no space character at all.
  if (/\s/.test(s) && (spec.id === 'EAN13' || spec.id === 'EAN8' || spec.id === 'Code128Ean')) return bad(SPACES_MESSAGE);
  switch (spec.id) {
    case 'EAN13': return validateEan('EAN-13', 13, s, auto);
    case 'EAN8': return validateEan('EAN-8', 8, s, auto);
    case 'Code128':
      if (!s.length) return bad('Code 128 needs some data');
      if (s.length > 80) return bad('Code 128 can hold at most 80 characters');
      if (!/^[\x20-\x7E]+$/.test(s)) return bad('Code 128 takes plain letters, digits and punctuation only');
      return ok(s);
    case 'Code128Ean': return validateGs1(s);
    case 'DataMatrix':
    case 'QR':
      if (!s.length) return bad(`${spec.label} needs some data`);
      if (s.length > 500) return bad(`${spec.label} can hold at most 500 characters`);
      if (spec.id === 'QR') {
        const max = QR_MAX_BYTES[errorCorrection] ?? QR_MAX_BYTES[spec.defaults.errorCorrection];
        if (new TextEncoder().encode(s).length > max) return bad('QR code data is too long for this error correction level');
      }
      return ok(s);
  }
  return bad('Unknown barcode type');
}

// ---- bwip-js ---------------------------------------------------------------------------------

const BCID = { EAN13: 'ean13', EAN8: 'ean8', Code128: 'code128', Code128Ean: 'gs1-128', DataMatrix: 'datamatrix', QR: 'qrcode' };
const QR_ECLEVEL = { QREC_0: 'L', QREC_1: 'M', QREC_2: 'Q', QREC_3: 'H' };

/** bwip-js draws 1 px per module for 1-D codes and 2 px per module for 2-D codes at scale 1. */
const BWIP_PX_PER_MODULE_2D = 2;

/**
 * Options for bwipjs.toSVG(). Rendered at scale 1 so that the SVG maps to file units by a UNIFORM
 * scale of bwipUnitsPerPx(field): 1-D codes are 1 px per module, and `height` (bwip takes mm at
 * 72 px/inch) is chosen so bar height in px = barHeight / narrowBarWidth modules — i.e. it is
 * barHeight/narrowBarWidth × 25.4/72, not barHeight/100.
 * No padding: quiet zones are accounted for by barcodeBox().
 */
export function bwipOptions(field) {
  const sym = field?.symbology;
  const bcid = BCID[sym] || 'code128';
  let text = String(field?.data ?? '');
  const opts = { bcid, text, scale: 1, paddingwidth: 0, paddingheight: 0 };
  const spec = symbologyById(sym);
  if (spec?.oneD) {
    const auto = field.autoCheckDigit ?? true;
    if ((sym === 'EAN13' || sym === 'EAN8') && auto) {
      const need = sym === 'EAN13' ? 12 : 7;
      const s = text.trim();
      if (s.length === need && /^\d+$/.test(s)) text = s + gtinCheckDigit(s);
    }
    opts.text = text;
    opts.includetext = (field.textDisplay ?? spec.defaults.textDisplay) !== 'DO_NOT_DISPLAY';
    // EAN human-readable digits sit in bwip's split guard layout; centring would break it.
    if (sym !== 'EAN13' && sym !== 'EAN8') opts.textxalign = 'center';
    const nbw = Number(field.narrowBarWidth) || spec.defaults.narrowBarWidth;
    const bh = Number(field.barHeight) || spec.defaults.barHeight;
    opts.height = Math.round(((bh / nbw) * 25.4 / 72) * 1000) / 1000;
  } else if (sym === 'QR') {
    opts.eclevel = QR_ECLEVEL[field.errorCorrection] || 'M';
  }
  return opts;
}

/** File units per SVG px for bwipOptions(field) output. */
export function bwipUnitsPerPx(field) {
  const spec = symbologyById(field?.symbology);
  if (!spec) return 1;
  if (spec.oneD) return Number(field.narrowBarWidth) || spec.defaults.narrowBarWidth;
  return (Number(field.cellWidth) || spec.defaults.cellWidth) / BWIP_PX_PER_MODULE_2D;
}

// ---- geometry ----------------------------------------------------------------------------------

/**
 * Human-readable text band below 1-D bars, in modules. GS1 nominal EAN-13: 25.93 mm total vs
 * 22.85 mm bars at X = 0.33 mm → 3.08 mm ≈ 9.3 modules; rounded up to 10 and used for all 1-D codes.
 */
export const HR_TEXT_MODULES = 10;

/** Code 128 quiet zone per side (modules). */
const C128_QUIET = 10;

const FNC1 = '\u001d';

/**
 * Approximate Code 128 symbol-character count (data only; excludes start/check/stop) using the usual
 * Auto rule: a digit run uses Code C when that is shorter than Code B, counting the 1-char switches
 * (a run at the start/end needs no switch on that side; an odd leftover digit stays in B). FNC1 counts
 * as one character and does not force a switch. Matches bwip-js for the tested cases; not a full
 * optimal encoder (Code A control chars, CodeA/B shift are ignored).
 */
export function code128SymbolCount(data, codeSet = 'Auto') {
  const s = String(data ?? '');
  if (codeSet === 'CodeA' || codeSet === 'CodeB') return s.length;
  if (codeSet === 'CodeC' && /^\d*$/.test(s.replaceAll(FNC1, '')) && s.replaceAll(FNC1, '').length % 2 === 0) {
    let n = 0;
    for (const seg of s.split(FNC1)) n += seg.length / 2;
    return n + (s.split(FNC1).length - 1);
  }
  // Tokenise into FNC1 / digit runs / other runs.
  const tokens = s.match(/\u001d|\d+|[^\d\u001d]+/g) || [];
  let n = 0;
  for (let i = 0; i < tokens.length; i++) {
    const t = tokens[i];
    if (t === FNC1) { n += 1; continue; }
    if (!/^\d/.test(t)) { n += t.length; continue; }
    const k = t.length;
    // Neighbour that is not FNC1 (FNC1 exists in every code set).
    const prev = tokens.slice(0, i).reverse().find((x) => x !== FNC1);
    const next = tokens.slice(i + 1).find((x) => x !== FNC1);
    let switches = (prev ? 1 : 0) + (next ? 1 : 0);
    if (k % 2 && switches === 0) switches = 1;
    const costC = Math.floor(k / 2) + (k % 2) + switches;
    n += Math.min(k, costC);
  }
  return n;
}

/** GS1 "(01)…(10)…" → FNC1-joined Code 128 data (FNC1 first, separators after variable-length AIs). */
function gs1ToCode128(data) {
  const parts = parseGs1(data);
  if (!parts) return FNC1 + String(data ?? '').replace(/[()]/g, '');
  let out = FNC1;
  parts.forEach((p, i) => {
    out += p.ai + p.value;
    if (i < parts.length - 1 && !GS1_FIXED_PREFIX.has(p.ai.slice(0, 2))) out += FNC1;
  });
  return out;
}

// ECC 200 square symbol sizes and data-codeword capacities.
const DM_SIZES = [
  [10, 3], [12, 5], [14, 8], [16, 12], [18, 18], [20, 22], [22, 30], [24, 36], [26, 44], [32, 62], [36, 86],
  [40, 114], [44, 144], [48, 174], [52, 204], [64, 280], [72, 368], [80, 456], [88, 576], [96, 696],
  [104, 816], [120, 1050], [132, 1304], [144, 1558],
];

/**
 * DataMatrix side length in modules (no quiet zone). Codewords estimated with ASCII encodation:
 * digit pairs = 1, other ASCII = 1, chars > 127 = 2. Exact for numeric data; over-estimates text that
 * a real encoder packs with C40/Text (so the box is conservative).
 */
export function dataMatrixModules(data) {
  const s = String(data ?? '');
  let cw = 0;
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    if (c >= 48 && c <= 57 && i + 1 < s.length && /\d/.test(s[i + 1])) { cw++; i++; } else cw += c > 127 ? 2 : 1;
  }
  return (DM_SIZES.find(([, cap]) => cap >= cw) || DM_SIZES.at(-1))[0];
}

// QR byte-mode capacity (characters) per version 1..25 for levels L, M, Q, H.
const QR_BYTES = [
  [17, 14, 11, 7], [32, 26, 20, 14], [53, 42, 32, 24], [78, 62, 46, 34], [106, 84, 60, 44],
  [134, 106, 74, 58], [154, 122, 86, 64], [192, 152, 108, 84], [230, 180, 130, 98], [271, 213, 151, 119],
  [321, 251, 177, 137], [367, 287, 203, 155], [425, 331, 241, 177], [458, 362, 258, 194], [520, 412, 292, 220],
  [586, 450, 322, 250], [644, 504, 364, 280], [718, 560, 394, 310], [792, 624, 442, 338], [858, 666, 482, 382],
  [929, 711, 509, 403], [1003, 779, 565, 439], [1091, 857, 611, 461], [1171, 911, 661, 511], [1273, 997, 715, 535],
];

/**
 * QR side length in modules (no quiet zone), from byte-mode capacity of the UTF-8 byte length.
 * Numeric/alphanumeric data would fit a smaller version in a real encoder, so this is conservative.
 */
export function qrModules(data, errorCorrection = 'QREC_1') {
  const bytes = new TextEncoder().encode(String(data ?? '')).length;
  const lvl = Math.max(0, QR_ECC.indexOf(errorCorrection));
  const idx = QR_BYTES.findIndex((row) => row[lvl] >= bytes);
  const version = idx === -1 ? QR_BYTES.length : idx + 1;
  return 17 + 4 * version;
}

/** Module count across a 1-D symbol, excluding quiet zones. */
export function linearModules(field) {
  switch (field?.symbology) {
    case 'EAN13': return 95;
    case 'EAN8': return 67;
    case 'Code128': return 11 * (code128SymbolCount(field.data, field.codeSet) + 3) + 2;
    case 'Code128Ean': return 11 * (code128SymbolCount(gs1ToCode128(field.data), field.codeSet) + 3) + 2;
  }
  return 0;
}

/**
 * Barcode extent in units, relative to the field origin, unrotated: { w, h }.
 * 1-D: w = (modules + quiet zones) × NarrowBarWidth, quiet zones only when QuietMargin is true
 *      (EAN-13 11+7, EAN-8 7+7, Code 128 / GS1-128 10+10); h = BarHeight + HR_TEXT_MODULES × NarrowBarWidth
 *      when human-readable text is displayed.
 * 2-D: side = (modules + quiet zone) × CellWidth; quiet zone 1+1 (DataMatrix) or 4+4 (QR) when QuietMargin.
 */
export function barcodeBox(field) {
  const spec = symbologyById(field?.symbology);
  if (!spec) return { w: 0, h: 0 };
  const d = spec.defaults;
  const quiet = field.quietMargin ?? d.quietMargin;
  if (spec.oneD) {
    const nbw = Number(field.narrowBarWidth) || d.narrowBarWidth;
    const bh = Number(field.barHeight) || d.barHeight;
    const q = !quiet ? 0 : spec.id === 'EAN13' ? 18 : spec.id === 'EAN8' ? 14 : 2 * C128_QUIET;
    const text = (field.textDisplay ?? d.textDisplay) !== 'DO_NOT_DISPLAY';
    return { w: (linearModules(field) + q) * nbw, h: bh + (text ? HR_TEXT_MODULES * nbw : 0) };
  }
  const cell = Number(field.cellWidth) || d.cellWidth;
  const modules = spec.id === 'QR'
    ? qrModules(field.data, field.errorCorrection ?? d.errorCorrection) + (quiet ? 8 : 0)
    : dataMatrixModules(field.data) + (quiet ? 2 : 0);
  return { w: modules * cell, h: modules * cell };
}
